import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import TemplateTable from '../shared/tables/TemplateTable';
import { useTheme } from '../../context/themeContext';
import { ITableHeader } from '../shared/tables/models/ITableHeader';
import { useTranslation } from 'react-i18next';
import { EllipsisDiv } from '../../styles/globalStyles';
import BiButton from '../primitives/buttons/BiButton.primitive';
import PickList, { PICKLIST_TYPES } from '../shared/Fields/PickList';
import { AppConstants } from '../../constants/AppConstants';
import InputField from '../shared/Fields/InputField';
import TextAreaField from '../shared/Fields/TextAreaField';
import { DualingListPick } from '../shared/DualingList/DualingList';
import Modal from '../shared/ModalPortal/Modal';
import { getPermissions } from '../../http/access-management-service';
import { HTTP_STATUS } from '../../http/constants/http.status';
import { differenceBy, isEmpty, orderBy, remove } from 'lodash';
import Card from '../shared/cards/Card';
import { careTeamIcon } from '../../assets/components/svgs';
import Pills from '../CarePlan/Reusable/Pills';
import UserRole from '../../assets/user-cog.svg';

import moment from 'moment';
import { IconButton } from '../primitives/buttons/IconButton';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { getOrganizations } from '../../http/organization-services';
import { setOrgList } from '../../app/rolesAndFeatureReducer';
import { getLoggedInOrg } from '../../util/admin-utils';
import { getUserLoggedInOrg } from '../../util/OrganizationUtil';
import { useDidMountEffect } from '../../util/customHooks';
import { ExclamationIcon } from '@heroicons/react/outline';
import InfoPopover from '../shared/Fields/InfoPopover';

interface Props {
  operations: Array<any>;
  setOperations: Function;
  rolePermissions: Array<any>;
  setRolePermissions: Function;
  getAPIPermissions: Function;
  disabled: boolean;
  getOrgForFeatures: Function;
}

const OperationsTag = ({
  operations,
  setOperations,
  rolePermissions,
  setRolePermissions,
  getAPIPermissions,
  disabled = false,
  getOrgForFeatures
}: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const orgList = useAppSelector((state) => state.rolesAndFeature?.orgList);
  const userProfile = useAppSelector((state) => state.userManagement.userProfileInfo);

  const [loadNextData, setLoadNextData] = useState<any>({
    orgPaging: false,
    searchOrgName: '',
    searchOrg: false,
    orgList: []
  });

  const [orgListLocal, setOrgListLocal] = useState([]);

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [deletePopup, setDeletePopup] = useState(false);
  const [operationToDelete, setOperationToDelete] = useState<number | null>(null);
  const [newOperation, setNewOperation] = useState<any>({
    actionName: '',
    description: '',
    status: [],
    apiPermissions: [],
    organizations: []
  });

  const [formError, setFormError] = useState<any>({
    actionName: '',
    description: '',
    status: [],
    apiPermissions: [],
    organizations: []
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    getAPIPermissions();
  }, []);

  useEffect(() => {
    const init = async () => {
      await fetchOrgList();
    };
    if (isModalOpen && isEmpty(orgList)) {
      init();
    }

    if (!isEmpty(orgList)) {
      setOrgListLocal(orgList);
    }
  }, [orgList, isModalOpen]);

  const fetchOrgList = async () => {
    try {
      const [loggedInOrg, orgResponse] = await Promise.all([
        getUserLoggedInOrg(userProfile),
        getOrgForFeatures()
      ]);

      let data = orgResponse || [];
      if (!isEmpty(loggedInOrg)) {
        data.unshift(loggedInOrg);
      }

      if (data.length > 0) {
        dispatch(setOrgList(data));
      }
      return data;
    } catch (error) {
      return [];
    }
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setNewOperation(operations[index]);
    setIsModalOpen(true);
  };
  const confirmDelete = () => {
    if (operationToDelete !== null) {
      setOperations(operations.filter((d: any, i) => i !== operationToDelete));
      setOperationToDelete(null);
    }
    setDeletePopup(false);
  };

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    const updatedOperations = [...operations];
    if (editingIndex !== null) {
      updatedOperations[editingIndex] = newOperation;
      setEditingIndex(null);
    } else {
      updatedOperations.push(newOperation);
    }
    setOperations(updatedOperations);
    setNewOperation({
      actionName: '',
      description: '',
      status: [],
      apiPermissions: [],
      organizations: []
    });
    setIsModalOpen(false);
  };

  let HEADERS: Array<ITableHeader> = [
    {
      key: 'status',
      sortEnabled: false,
      description: `${t('T_STATUS')}`,
      width: '10%',
      sortingInformation: {
        order: ''
      },
      render: (data: any) => {
        return data?.status?.length ? data?.status[0]?.value : '';
      }
    },
    {
      key: 'actionName',
      description: `${t('T_FEATURE_OPERATION_ACTION_NAME')}`,
      sortEnabled: false,
      width: '20%',
      clickable: true,
      sortingInformation: {
        order: ''
      },
      styles: { borderRight: 'none' }
    },

    {
      key: 'description',
      description: `${t('T_DESCRIPTION')}`,
      sortEnabled: false,
      width: '25%',
      nastedData: true,
      sortingInformation: {
        order: ''
      },
      render: (data: any) => {
        let description = data?.description;
        return <EllipsisDiv title={description}>{description}</EllipsisDiv>;
      }
    },
    {
      key: 'apiPermissions',
      sortEnabled: false,
      description: `${t('T_FEATURE_OPERATION_API_PERMISSIONS')}`,
      width: disabled ? '45%' : '35%',
      nastedData: false,
      sortingInformation: {
        order: ''
      },

      render: (data: any) => {
        let permissions = data?.apiPermissions
          .map((permission: any) => permission.apiPermission)
          .filter((apiPermission: any) => apiPermission)
          .join(', ');
        return (
          <EllipsisDiv className="w-full flex-1" title={permissions}>
            {permissions}
          </EllipsisDiv>
        );
      }
    }
  ];

  const [tableHeaders, setTableHeaders] = useState(HEADERS);
  const [size, setSize] = useState(AppConstants.MODULE_LIST_DEFAULT_PAGE_SIZE.toString());
  const [currentPage, setCurrentPage] = useState('1');
  const [currentActivePage, setCurrentActivePage] = useState(0);

  useEffect(() => {
    setCurrentActivePage(parseInt(currentPage) * parseInt(size) - parseInt(size));
  }, [currentPage]);

  useEffect(() => {
    if (!disabled) {
      setTableHeaders([
        ...HEADERS,
        {
          key: 'buttons',
          description: `${t('T_ACTIONS')}`,
          sortEnabled: false,
          width: '10%',
          sortingInformation: {
            order: ''
          }
        }
      ]);
    }
  }, [disabled]);

  const validator = () => {
    const { actionName, description, status, apiPermissions } = newOperation;
    if (!actionName.length || !description.length || !status.length || !apiPermissions.length) {
      return true;
    }

    return false;
  };

  const searchOrgData = (data: any) => {
    setLoadNextData((prevState: any) => ({
      ...prevState,
      searchOrgName: data,
      searchOrg: true,
      orgPaging: false
    }));
  };

  const fetchMoreOrgData = () => {
    setLoadNextData((prevState: any) => ({ ...prevState, orgPaging: true }));
  };

  useDidMountEffect(async () => {
    if (loadNextData.searchOrg) {
      loadOrgSearchData();
    }
  }, [loadNextData]);

  const filterOrgNames = (keyword: string, orgList: any = []) => {
    return orgList.filter((org: any) => org.name.toLowerCase().includes(keyword.toLowerCase()));
  };

  const loadOrgSearchData = async () => {
    const orgResponse = filterOrgNames(loadNextData.searchOrgName, orgList || []);
    setOrgListLocal(orgResponse);
  };

  const handleDelete = (index: number) => {
    setOperationToDelete(index);
    setDeletePopup(true);
  };

  return (
    <div className="h-full w-full p-4">
      <Card
        title={t('T_FEATURE_OPERATION_MANAGEMENT')}
        icon={UserRole}
        alt={t('T_FEATURE_OPERATION_MANAGEMENT')}
        height=""
        width=""
        actions={
          <InfoPopover
            className="ml-2"
            content={
              <div className="mx-2 my-1.5 w-48 text-sm font-normal">
                <div>{t('T_FEATURE_OPERATION_VALIDATION_MESSAGE')}</div>
              </div>
            }
          ></InfoPopover>
        }
      >
        <div className="mb-4 flex w-full">
          <BiButton
            className={'ml-auto bg-primary py-1.5 text-white'}
            type="button"
            disabled={disabled}
            testId={t('T_FEATURE_ADD_NEW_OPERATION')}
            onClick={() => {
              setEditingIndex(null);
              setNewOperation({
                actionName: '',
                description: '',
                status: [],
                apiPermissions: [],
                organizations: []
              });
              setIsModalOpen(true);
            }}
          >
            {t('T_FEATURE_ADD_NEW_OPERATION')}
          </BiButton>
        </div>
        <TemplateTable
          isLoading={false}
          tableData={
            operations.length
              ? operations.slice(currentActivePage, currentActivePage + parseInt(size))
              : `${t('T_NO_DATA_AVAILABLE_MSG')}`
          }
          size={size}
          setSize={setSize}
          currentPage={currentPage}
          cellSelector={() => {}}
          templateSelector={() => {}}
          onRefreshTableData={() => {}}
          tableHeaders={tableHeaders}
          setCurrentPage={setCurrentPage}
          setTableHeaders={setTableHeaders}
          containerClass="mt-0 w-full"
          searchPlaceholder={t('T_FEATURE_SEARCH_PLACEHOLDER') || ''}
          isPaginated={false}
          isLocalPaginator
          theme={useTheme().theme}
          isSearchable={false}
          totalDataLength={operations.length}
          showEditDeleteButton={!disabled}
          editOnClick={(index: number) => {
            if (!disabled)
              handleEdit(
                currentPage === '1' ? index : parseInt(size) * (parseInt(currentPage) - 1) + index
              );
          }}
          deleteOnClick={(index: number) => {
            if (!disabled)
              handleDelete(
                currentPage === '1' ? index : parseInt(size) * (parseInt(currentPage) - 1) + index
              );
          }}
          // defaultPillsLabel={t('T_CONFIG_CATEGORIES_FILTERS_DEFAULT_VIEW')}
          tablePage={'operation-list'}
        />
      </Card>

      <Modal
        showHeader={true}
        showCloseIcon={true}
        width="80rem"
        headerText={editingIndex !== null ? 'Edit Operation' : 'Add New Operation'}
        toggleVisiblity={(value: boolean) => {
          setIsModalOpen(false);
        }}
        isVisible={isModalOpen}
        overflowVisible={true}
        className=""
        //body={<NewQuestion editFromSurveyTemplate={true} questionId={editedQaId} />}
        body={
          <div className="h-full w-full items-center justify-center">
            <div className="flex w-full flex-row space-x-4">
              <div className="flex w-2/3 flex-col">
                <InputField
                  type={'text'}
                  id={'actionName'}
                  label={t('T_FEATURE_OPERATION_ACTION_NAME')}
                  placeholder={''}
                  fieldKey={'actionName'}
                  asterixFirst={true}
                  required={true}
                  formData={newOperation}
                  setFormData={setNewOperation}
                  formError={formError}
                  disabled={false}
                  max={AppConstants.MAX_LENGTH_IMPORT_NAME}
                />
              </div>
              <div className="w-1/3">
                <PickList
                  fieldKey={'key'}
                  fieldValues={['value']}
                  label={t('T_STATUS')}
                  selectedItems={newOperation.status}
                  onSelectedChange={(items: any) => {
                    setNewOperation({ ...newOperation, status: items });
                  }}
                  items={AppConstants.FEATURES_STATUS_VALUES}
                  disabled={false}
                  readOnly={false}
                  type={PICKLIST_TYPES.SINGLE}
                  required
                />
              </div>
            </div>
            <div className="mt-4 flex w-full flex-col">
              <TextAreaField
                fieldKey={'description'}
                type={'text'}
                id={'description'}
                label={t('T_DESCRIPTION')}
                testId="operations-description"
                placeholder={''}
                required
                formData={newOperation}
                setFormData={setNewOperation}
                formError={formError}
                asterixFirst
                rows={3}
                disabled={false}
              ></TextAreaField>
            </div>
            <div className="mt-4 flex w-full flex-wrap">
              <PickList
                fieldKey={'id'}
                fieldValues={['name']}
                label={t('T_FEATURE_ORGANIZATIONS')}
                selectedItems={newOperation.organizations}
                onSelectedChange={(items: any) => {
                  setNewOperation({ ...newOperation, organizations: items });
                }}
                items={orgListLocal || []}
                disabled={false}
                readOnly={false}
                type={PICKLIST_TYPES.MULTI}
                showPills
                required={false}
                pillsData={newOperation.organizations}
                pillId="feature-operation-organizations"
                pillMaxWidth="100%"
                pillsLabel={
                  newOperation.organizations?.length > 0
                    ? t('T_FEATURE_SELECTED_ORGANIZATIONS')
                    : ''
                }
                pillsRows={3}
                disablePill={disabled}
                removeFilter={(item: any) => {
                  const updatedOrgs = remove(
                    [...newOperation.organizations],
                    (obj) => obj.id !== item?.id
                  );
                  setNewOperation({ ...newOperation, organizations: updatedOrgs });
                }}
                searchable
                fetchMoreData={fetchMoreOrgData}
                searchData={searchOrgData}
              />
            </div>
            <div className="mt-4 flex w-full flex-wrap">
              <DualingListPick
                initialData={differenceBy(
                  rolePermissions,
                  newOperation.apiPermissions,
                  'apiPermission'
                )}
                setInitialData={setRolePermissions}
                outputData={newOperation.apiPermissions}
                setOutputData={(data: any) =>
                  setNewOperation({ ...newOperation, apiPermissions: data })
                }
                fieldKey="id"
                fieldValues={['apiPermission']}
                inputHeader={t('T_ROLE_API_PERMISSIONS_AVAILABLE')}
                outputHeader={t('T_ROLE_API_PERMISSIONS_SELECTED')}
                orderingControls={false}
                disabled={false}
                styles={{
                  listWidth: 'w-full',
                  listHeight: 'h-[20rem]',
                  buttonHeight: 'h-8',
                  buttonWidth: 'w-8'
                }}
              ></DualingListPick>
            </div>
          </div>
        }
        secondaryOnClick={() => setIsModalOpen(false)}
        secondaryButtonText={t('T_CANCEL')}
        secondaryButtonWidth={'100px'}
        primaryButtonText={t('T_SAVE')}
        primaryButtonWidth={'100px'}
        primaryOnClick={handleSave}
        primaryButtonDisabled={validator()}
      />

      <Modal
        showCloseIcon={true}
        width="48rem"
        headerText={t('T_FEATURE_REMOVE_OPERATION__HEADER')}
        toggleVisiblity={(value: boolean) => {
          setDeletePopup(value);
        }}
        isVisible={deletePopup}
        body={
          <div>
            <div>{t('T_FEATURE_REMOVE_OPERATION_HELPER')}</div>
            <div className="mt-8 flex">
              <BiButton
                className={'ml-auto mr-2 text-primary'}
                type="button"
                onClick={() => {
                  setDeletePopup(false);
                }}
              >
                {t('T_CANCEL')}
              </BiButton>
              <BiButton className={'bg-primary text-white'} type="button" onClick={confirmDelete}>
                {t('T_FEATURE_REMOVE_OPERATION_BTN')}
              </BiButton>
            </div>
          </div>
        }
        headerLeftIcon={<ExclamationIcon className="h-7.5 w-6" />}
      />
    </div>
  );
};

export default OperationsTag;
